import { Injectable } from '@angular/core';
import { ApiService, Endpoint } from './api.service';
import {
  Observable,
  MonoTypeOperatorFunction,
  ReplaySubject,
  combineLatest,
} from 'rxjs';
import { filter, map, shareReplay, skip, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { UserGroupAttributes } from '~app/models/user-group';
import { AuthService } from './auth.service';
import { PersonAttributes } from '~app/models/person';
import { AssignedRole } from '~app/models/assigned-role';
@Injectable({
  providedIn: 'root',
})
export class UserGroupService {
  private endpoint: Endpoint<UserGroupAttributes>;
  public readonly _refreshList$ = new ReplaySubject<void>(1);
  private _userGroupById: { [Id: string]: UserGroupAttributes } = {};

  public readonly op_sortGroups: MonoTypeOperatorFunction<
    UserGroupAttributes[]
  > = (userGroups$) => {
    return userGroups$.pipe(
      tap((userGroups) => {
        userGroups.sort(this.compareForSort);
      })
    );
  };

  public readonly allUserGroups$ = this.authService.currentUser$.pipe(
    filter((user) => !!user),
    take(1),
    switchMap(() => this._refreshList$),
    switchMap((_) => this.endpoint.GetAll()),
    tap((userGroups) => {
      userGroups.forEach((userGroup) => {
        this._userGroupById[userGroup.id] = userGroup;
      });
      userGroups.forEach((userGroup) => {
        // Moving this filter here to avoid doing this in more numberous places.
        const children = userGroups.filter(
          (child) => child.parentId === userGroup.id
        );
        children.forEach((child) => (child.parent = userGroup));
        // Using Object.defineProperty to prevent it from showing up in JSON stringify
        Object.defineProperty(userGroup, 'children', {
          value: children,
        });
      });
      userGroups.forEach((group) => {
        // examine all parents to set specific info
        let parents = [
          group.parent?.parent?.parent,
          group.parent?.parent,
          group.parent,
        ].filter((p) => p);
        group.regionStation = parents.find((p) => p?.tierLabel === 'Region');
        group.forestLab = parents.find((p) => p?.tierLabel === 'ForestLab');
        let parentPrefix = parents
          .map((p) => p.abbreviation)
          .filter((s) => s)
          .join(' | ');
        if (parentPrefix) {
          parentPrefix += ' | ';
        }
        let ownPrefix = group.abbreviation ? group.abbreviation + ' - ' : '';
        group.labelPrefix = parentPrefix + ownPrefix;
      });
    }),
    shareReplay(1)
  );
  public readonly allUserGroupsById$: Observable<{ [Id: string]: UserGroupAttributes }> = this.allUserGroups$.pipe(
    map((_) => this._userGroupById)
  );

  public readonly allTeams$ = this.allUserGroups$.pipe(
    map((groups) => groups.filter((group) => group.tierLabel === 'Team')),
    this.op_sortGroups,
    shareReplay(1),
    map((groups) => groups.slice())
  );
  // for region dropdown vals
  public readonly allForestLabs$ = this.allUserGroups$.pipe(
    map((groups) => groups.filter((group) => group.tierLabel === 'ForestLab')),
    this.op_sortGroups,
    shareReplay(1),
    map((groups) => groups.slice())
  );
  public readonly allRegions$ = this.allUserGroups$.pipe(
    map((groups) => groups.filter((group) => group.tierLabel === 'Region')),
    this.op_sortGroups,
    shareReplay(1),
    map((userGroups) => userGroups.slice())
  );

  public readonly systemUserGroup$ = this.allUserGroups$.pipe(
    map((groups) => groups.find((group) => group.tier === 0)),
    shareReplay(1)
  );

  public readonly userGroupsByTier$ = combineLatest([
    this.allTeams$,
    this.allForestLabs$,
    this.allRegions$,
    this.systemUserGroup$,
  ]).pipe(
    map(([teams, forests, regions, system]) => ({
      teams,
      forests,
      regions,
      system,
    }))
  );

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {
    this.endpoint = apiService.endpoint('user-groups');

    this.loadAllUserGroups();
  }

  findById(id: string): Observable<UserGroupAttributes> {
    return this.allUserGroups$.pipe(
      map((userGroup: UserGroupAttributes[]) => {
        return userGroup.find((userGroup) => userGroup.id === id);
      })
    );
  }

  get op_FindByIdAfterRefresh() {
    // wrapped inside a get() because `this._refreshList$` has `this` as undefined.
    return (id$: Observable<string>) => {
      return id$.pipe(
        tap((_) => this._refreshList$.next()),
        switchMap((id) =>
          this.allUserGroups$.pipe(
            skip(1),
            map((_) => id)
          )
        ),
        switchMap((id) => this.findById(id))
      );
    };
  }

  compareForSort(g1: UserGroupAttributes, g2: UserGroupAttributes) {
    if (!g1 || !g2) {
      return 0;
    }
    if (g1.parentId === g2.parentId) {
      return g1.sortOrder - g2.sortOrder;
    }
    let g1Group = [g1, g1.parent, g1.parent?.parent, g1.parent?.parent?.parent]
      .filter((x) => !!x)
      .reverse();
    let g2Group = [g2, g2.parent, g2.parent?.parent, g2.parent?.parent?.parent]
      .filter((x) => !!x)
      .reverse();

    // Top-most parent will be gxGroup[0].  Will always be System.
    // [1] will be region.
    // [2] will be Forest/Lab (or tier2 Team) or non-existent
    // [3] will be Team or non-existent
    return (
      (g1Group[1]?.sortOrder || 0) - (g2Group[1]?.sortOrder || 0) ||
      (g1Group[2]?.sortOrder || 0) - (g2Group[2]?.sortOrder || 0) ||
      (g1Group[3]?.sortOrder || 0) - (g2Group[3]?.sortOrder || 0)
    );
  }

  /**
   * Returns the user's scoped group IDs and isAdmin flag from the backend team-scoping middleware.
   * For system_admin: isAdmin=true, allGroupIds=[] (sees everything).
   * For region_admin: isAdmin=false, allGroupIds=[their region + all descendants].
   */
  myScopedGroupIds$: Observable<{ isAdmin: boolean; allGroupIds: string[] }> =
    this.authService.personId$.pipe(
      switchMap(() =>
        this.apiService.GetSingle<{ isAdmin: boolean; allGroupIds: string[] }>(
          'actions/my-scoped-group-ids'
        )
      ),
      shareReplay(1)
    );

  getSystemWhereImAdmin() {
    return this.searchMyUserGroups(['system_admin']);
  }
  getRegionsWhereImRegionAdmin() {
    return this.searchMyUserGroups(['region_admin']);
  }
  getTeamsWhereImSupervisor() {
    return this.searchMyUserGroups(['supervisor']);
  }
  getTeamsWhereImMember() {
    return this.searchMyUserGroups(['team_member', 'supervisor']);
  }

  searchMyUserGroups(roleIds: string[]) {
    return this.authService.personId$.pipe(
      switchMap((personId) =>
        // log in here maybe to make sure its not poppin off
        {
          return this.searchUserGroups({
            personIds: [personId],
            roleIds: roleIds,
          });
        }
      )
    );
  }
  searchUserGroups(searchBody: { personIds?: string[]; roleIds?: string[] }) {
    let myGroupsPartial$ = this.apiService
      .Search<UserGroupAttributes[]>('actions/search-user-groups', searchBody);
    let final$ = combineLatest([
      myGroupsPartial$,
      this.allUserGroupsById$,
    ]).pipe(
      map(([myGroups, allGroupsById]) =>
        myGroups.map((group) => allGroupsById[group.id])
      )
    );
    return final$;
  }

  /**
   * Returns lists of users with the given roles, separated by the given roles.
   * - Should return all users and all their roles.
   * - Should return an empty list for any specified roles that doesn't have a user attached.
   */
  getMembersByRole<T extends string>(
    userGroupId: string,
    roleIds: readonly T[]
  ): Observable<{
    [roleId in T]: PersonAttributes[];
  }> {
    return this.apiService
      .Get<(PersonAttributes & { AssignedRoles: AssignedRole[] })[]>(
        '/actions/user-group-member-list/',
        userGroupId
      )
      .pipe(
        map((people: (PersonAttributes & { AssignedRoles: AssignedRole[] })[]) => {
          const lists = {} as { [roleId: string]: PersonAttributes[] };
          // Only count role assignments on the requested team. The API merges
          // root-level guest assignments into each person's AssignedRoles so
          // global guest badges work, but the team-edit modal needs strictly
          // team-scoped membership — otherwise root guests would surface in
          // the team's Guests list as soon as they happen to hold another
          // role here.
          people.forEach((person) => {
            person.AssignedRoles.forEach((role) => {
              if (role.UserGroupId !== userGroupId) return;
              lists[role.RoleId] = lists[role.RoleId] ?? [];
              // A person can carry the same role twice (e.g. a team-scoped guest
              // role that also gets merged in server-side), so guard against
              // listing them more than once per role.
              if (lists[role.RoleId].some((p) => p.id === person.id)) return;
              lists[role.RoleId].push(person);
            });
          });
          return lists;
        }),
        map((response) => {
          roleIds?.forEach((id) => (response[id] = response[id] ?? []));
          // Wish I didn't have to return this with an `as`.
          return response as { [roleId in T]: PersonAttributes[] };
        })
      );
  }

  /**
   * Return dropdown lists for Crew Member and Supervisor.
   * - Allow Supervisors to be an option in the Crew Member dropdown list.
   * - Reusable across different trip forms
   *
   * Guests are excluded from POC and Additional POC since they cannot be
   * assigned to those roles on a trip. They remain eligible as Crew Members
   * (matches the backend rule in crew-member-meta.model.js).
   */
  getCrewMemberDropdownLists(userGroupId: string) {
    return this.getMembersByRole(userGroupId, [
      'team_member',
      'supervisor',
      'guest',
    ]).pipe(
      map((listsByRole) => {
        const guestIds = new Set(listsByRole.guest.map((p) => p.id));
        const excludeGuests = (people: PersonAttributes[]) =>
          people.filter((p) => !guestIds.has(p.id));

        let supervisorsMinusMembers = listsByRole.supervisor.filter(
          (sup) => !listsByRole.team_member.find((mem) => mem.id === sup.id)
        );
        // Guests remain eligible as Crew Members (see method doc). Add any
        // guest not already present as a team_member/supervisor, deduped by id.
        let crewBase = [...listsByRole.team_member, ...supervisorsMinusMembers];
        let crewBaseIds = new Set(crewBase.map((p) => p.id));
        let guestsNotAlreadyCrew = listsByRole.guest.filter(
          (g) => !crewBaseIds.has(g.id)
        );
        let crewMembers = [...crewBase, ...guestsNotAlreadyCrew];
        let pocs = excludeGuests(listsByRole.supervisor);
        let additionalPocs = excludeGuests(crewMembers);

        // For the guest badge: pass only the person id and let
        // GuestRoleService resolve guest status across ALL teams.
        // (The AssignedRoles on these person objects are scoped to this user
        // group, so they'd miss a guest assignment in any other team.)
        return {
          crew_member: crewMembers
            .map((person) => ({
              value: person,
              label: person.displayNameDropdown,
              disabled: false,
              badgePerson: { id: person.id },
            }))
            .sort((a, b) =>
              a.label > b.label ? 1 : a.label < b.label ? -1 : 0
            ),
          supervisor: pocs
            .map((person) => ({
              value: person,
              label: person.displayNameDropdown,
              disabled: false,
              badgePerson: { id: person.id },
            }))
            .sort((a, b) =>
              a.label > b.label ? 1 : a.label < b.label ? -1 : 0
            ),
          additional_poc: additionalPocs
            .map((person) => ({
              value: person,
              label: person.displayNameDropdown,
              disabled: false,
              badgePerson: { id: person.id },
            }))
            .sort((a, b) =>
              a.label > b.label ? 1 : a.label < b.label ? -1 : 0
            ),
        };
      })
    );
  }

  loadAllUserGroups() {
    this._refreshList$.next();
    return this.allUserGroups$.pipe(skip(1));
  }

  addUserGroup(input: UserGroupAttributes): Observable<UserGroupAttributes> {
    let request = this.endpoint.Post(input);
    return request.pipe(
      map((group: UserGroupAttributes) => group.id),
      this.op_FindByIdAfterRefresh
    );
  }

  saveUserGroup(input: UserGroupAttributes): Observable<UserGroupAttributes> {
    let request: Observable<UserGroupAttributes>;
    if (input.id) {
      request = this.endpoint.Put(input.id, input);
    } else {
      request = this.endpoint.Post(input);
    }
    return request.pipe(
      map((group) => group.id),
      this.op_FindByIdAfterRefresh
    );
  }

  deleteUserGroup(id: uuid): Observable<void> {
    let request = this.endpoint.Delete(id);
    return request.pipe(
      tap((_) => this._refreshList$.next()),
      map((_) => null)
    );
  }
}
